import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TasksContent } from "./TasksContent";
import { TasksFilterWrapper } from "./TasksFilterWrapper";
import { TasksActions } from "./TasksActions";
import { CreateTaskForm } from "./CreateTaskForm";
import { EditTaskModal } from "./EditTaskModal";
import { useTasks } from "@/hooks/tasks/useTasks";
import { TasksFilterProvider, useTasksFilter } from "@/contexts/TasksFilterContext";
import { usePersistedViewMode } from "@/utils/viewMode";
import { usePageTitle } from "@/hooks/usePageTitle";
import { supabase } from "@/integrations/supabase/client";
import { useEntityHistory } from "@/hooks/useEntityHistory";

const TasksPageContent = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const { ownershipFilter } = useTasksFilter();
  const { trackVisit } = useEntityHistory();
  
  const { data: tasks = [], isLoading, error, refetch } = useTasks(ownershipFilter);
  
  // Get task ID from URL on mount (before any filter updates)
  const taskIdFromUrl = searchParams.get('task');
  
  // Update page title based on selected task
  usePageTitle(
    selectedTask ? `${selectedTask.name} - ${t("tasks.header", "Tasks")}` : t("tasks.header", "Tasks")
  );

  // Handle URL parameter for opening task
  useEffect(() => {
    if (!taskIdFromUrl) {
      if (selectedTask) {
        setSelectedTask(null);
      }
      return;
    }
    
    // Skip if we already have this task selected
    if (selectedTask?.id === taskIdFromUrl)
      return;
      
    // First check if task is in current list
    const task = tasks.find(t => t.id === taskIdFromUrl);
    if (task) {
      setSelectedTask(task);
      // Track visit when task is selected
      trackVisit('task', task.id, task.name, {
        taskStatus: task.status,
        taskPriority: task.priority
      });
      return;
    }
    
    // Task not in filtered list, fetch it directly (bypassing ownership filter)
    if (!isLoading) {
      supabase
        .from('tasks')
        .select('*')
        .eq('id', taskIdFromUrl)
        .maybeSingle()
        .then(({ data, error }) => {
          if (data && !error) {
            setSelectedTask(data);
            // Track visit when task is fetched
            trackVisit('task', data.id, data.name, {
              taskStatus: data.status,
              taskPriority: data.priority
            });
          }
        });
    }
  }, [taskIdFromUrl, tasks, isLoading, selectedTask, trackVisit]);

  // Handle closing task and updating URL
  const handleCloseTask = () => {
    setSelectedTask(null);
    // Remove task parameter from URL
    searchParams.delete('task');
    setSearchParams(searchParams, { replace: true });
  };

  if (error) {
    return (
      <div>
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {t("common.error", "Error")}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t("tasks.error.loading", "Failed to load tasks. Please try again.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t("tasks.header", "Tasks")}
        actions={
          <TasksActions
            onCreateTask={() => setIsCreateDialogOpen(true)}
          />
        }
      />
      
      <TasksFilterWrapper />
      <TasksContent tasks={tasks} isLoading={isLoading} onTaskUpdated={refetch} />
      
      <CreateTaskForm
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={() => {
          setIsCreateDialogOpen(false);
          refetch();
        }}
      />

      {selectedTask && (
        <EditTaskModal
          task={selectedTask}
          isOpen={true}
          onClose={handleCloseTask}
          onSave={() => {
            handleCloseTask();
            refetch();
          }}
        />
      )}
    </div>
  );
};

export const TasksPage = () => {
  const [viewMode, setViewMode] = usePersistedViewMode("tasks", "list");
  
  return (
    <TasksFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
      <TasksPageContent />
    </TasksFilterProvider>
  );
};
